import { Component, AfterViewInit, OnDestroy,ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DataService } from "src/app/services/data.service";
import { GetInformacionService } from "src/app/services/perfilamiento/get-informacion.service";
import { DialogService } from "src/app/services/dialog.service";
import { CambioestadocarpetaService } from "src/app/services/perfilamiento/cambioestadocarpeta.service";
import { PutFolderUpdateService } from "src/app/services/perfilamiento/update-status-folder.service";

interface TryInfo {
  totalIntentos: number;
  tipoValidacion: number;
  intentosActuales: number;
}

const VALIDATION_TYPE_WS = 6;
const VALIDATION_TYPE_OTP = 4;
const REJECTION_STATUS_CODE = "900000";
const NEW_SMS_STATE = "C25";

const BLOCK_DURATION_SECONDS = 60;
const POLLING_INTERVAL_MS = 5000;
const LIMITED_VALIDATION_TIMEOUT_MS = 60000; // 1 minuto

@Component({
  selector: "app-validation-ws",
  templateUrl: "./validation-ws.component.html",
  styleUrls: ["../validacion/validacion.component.scss"],
})
export class ValidationWsComponent implements AfterViewInit, OnDestroy {
  numeroCarpeta = "";
  cellphone = "";
  enviarWs: TryInfo = {
    totalIntentos: 0,
    tipoValidacion: VALIDATION_TYPE_WS,
    intentosActuales: 0,
  };
  enviarOtp: TryInfo = {
    totalIntentos: 0,
    tipoValidacion: VALIDATION_TYPE_OTP,
    intentosActuales: 0,
  };
  bloqueado = false;
  tiempoRestante = BLOCK_DURATION_SECONDS;
  isLoading = false;

  public manualValidationStarted = false;
 colorAdvertencia: "success" | "warning" | "info" = "success";
  textoAdvertencia = "";
  showAdvertencia: boolean = false;
  loadingAdvertencia = false;
  private blockIntervalId?: number;
  private blockTimeoutId?: number;
  private validationIntervalId?: number;
  private validationTimeoutId?: number;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private data: DataService,
    private getinfoServ: GetInformacionService,
    private cambioEstadoServ: CambioestadocarpetaService,
    private dialogService: DialogService,
    private whatsapp: GetInformacionService,
    private putFolderUpdateStatus: PutFolderUpdateService,
    private cd: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.dialogService.openDialogLoadingService("");

    if (!this.data.numeroCarpeta) {
      this.dialogService.closeDialog();
      return;
    }
    this.numeroCarpeta = this.data.numeroCarpeta;

    this.loadClientInfo()
      .then(() => {
        if (!this.data.isResume) {
          this.dialogService.closeDialog();
          this.openDialogSend();
        } else {
          this.dialogService.closeDialog();
        }
      })
      .catch((err) => this.handleErrorCommon(err, "cargar información"));
  }

  ngOnDestroy(): void {
    this.clearBlockTimers();
    this.clearValidationTimers();
    this.dialogService.closeDialog();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- DIALOG DE ENVÍO DE WHATSAPP ---

  public async openDialogSend(): Promise<void> {
    await this.loadClientInfo().catch((err) =>
      this.handleErrorCommon(err, "actualizar intentos")
    );

    const currentAttempt =
      this.enviarWs.totalIntentos - this.enviarWs.intentosActuales + 1;
    const isFirst = currentAttempt === 1;

    this.dialogService.closeDialog();
    this.dialogService.openDialog(
      isFirst ? "Enviar mensaje WhatsApp" : "Reenviar mensaje WhatsApp",
      `El solicitante recibirá un mensaje por WhatsApp para aceptar los T&C al celular <b>${this.cellphone}</b> para iniciar el proceso de autorización en centrales de riesgo.<br/><br/>
      <strong>Intento ${currentAttempt} de ${this.enviarWs.totalIntentos}</strong>`,
      true,
      isFirst ? "Enviar WhatsApp" : "Reenviar WhatsApp",
      () => this.sendWhatsApp()
    );
  }

  private sendWhatsApp(): void {
    this.startBlock();

    this.whatsapp
      .sendWhatsAppMessage({
        carpeta: this.numeroCarpeta,
        phone: this.cellphone,
        template: "bayportinforma",
        code: `avanzar con tu solicitud de crédito, es necesario que autorices el tratamiento de tus datos personales y la consulta en centrales de riesgo. Responde con *AUTORIZO* para aceptar los términos descritos en: https://datos.bayport-digital.com/. Al hacerlo, confirmas que has leído y comprendido tus derechos, así como la forma de ejercerlos.`,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loadClientInfo().then(() => this.startValidationPolling());
        },
        (err) => {
          this.handleErrorCommon(err, "enviar WhatsApp");
        }
      );
  }

  /**
   * Inicia un bloqueo de reenvío de BLOCK_DURATION_SECONDS segundos.
   * Durante ese tiempo, se decrementa tiempoRestante y se inhabilita el enlace “Reenviar”.
   */
  private startBlock(): void {
    this.clearBlockTimers();

    this.bloqueado = true;
    this.tiempoRestante = BLOCK_DURATION_SECONDS;

    this.blockIntervalId = window.setInterval(() => {
      this.tiempoRestante--;
      if (this.tiempoRestante <= 0) {
        this.endBlock();
      }
    }, 1000);

    this.blockTimeoutId = window.setTimeout(() => {
      this.endBlock();
    }, BLOCK_DURATION_SECONDS * 1000);
  }

  private clearBlockTimers(): void {
    if (this.blockIntervalId != null) {
      clearInterval(this.blockIntervalId);
      this.blockIntervalId = undefined;
    }
    if (this.blockTimeoutId != null) {
      clearTimeout(this.blockTimeoutId);
      this.blockTimeoutId = undefined;
    }
  }

  /**
   * Finaliza el bloqueo: restablece bandera, limpia timers, recarga intentos y ofrece canales alternativos
   */
  private async endBlock(): Promise<void> {
    this.bloqueado = false;
    this.clearBlockTimers();

    await this.loadClientInfo().catch((err) =>
      this.handleErrorCommon(err, "actualizar canales")
    );
    this.offerAlternativeChannels();
  }

  /**
   * Muestra diálogo de alternativas según si quedan intentos de WS o de OTP
   */
  private offerAlternativeChannels(): void {
    const wsAvailable = this.enviarWs.intentosActuales > 0;
    const smsAvailable = this.enviarOtp.intentosActuales > 0;

    if (!wsAvailable && !smsAvailable) {
      // this.dialogService.openDialog(
      //   "Intentos agotados",
      //   "Has agotado todos los intentos.",
      //   false,
      //   "Aceptar"
      // );
    } else if (!wsAvailable && smsAvailable) {
      this.dialogService.openDialog(
        "Agotaste los intentos por este medio",
        "Excediste el número de envíos por WhatsApp. Para continuar, elige “Enviar SMS o “Regresar” para volver a la pantalla anterior.",
        true,
        "Enviar SMS",
        () => this.sendSms(),
        true,
        "Regresar",
      );
    }
  }

  // --- POLLING NORMAL (POST-ENVÍO DE WHATSAPP) ---
  private startValidationPolling(): void {
    this.clearValidationTimers();

    this.validationIntervalId = window.setInterval(() => {
      this.whatsapp
        .validateAuthorizationWhatsApp(this.cellphone)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => this.handleValidationResponse(res.status),
          (err) => this.handleErrorCommon(err, "validar autorización")
        );
    }, POLLING_INTERVAL_MS);
  }

  /** Cancela el intervalo de polling normal si estaba activo */
  private clearValidationTimers(): void {
    if (this.validationIntervalId != null) {
      clearInterval(this.validationIntervalId);
      this.validationIntervalId = undefined;
    }
    if (this.validationTimeoutId != null) {
      clearTimeout(this.validationTimeoutId);
      this.validationTimeoutId = undefined;
    }
  }

  // --- POLLING LIMITADO CON SPINNER VISIBLE 1 MINUTO ---

  public startValidationPollingLimited(): void {
    this.isLoading = true;
    // Alert
    this.alertaValidacion(this.isLoading)
    this.manualValidationStarted = true;
    this.startBlock();
    this.clearValidationTimers();
    this.validationIntervalId = window.setInterval(() => {
      this.whatsapp
        .validateAuthorizationWhatsApp(this.cellphone)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => this.handleValidationResponse(res.status),
          (err) => this.handleErrorCommon(err, "validación limitada")
        );
    }, POLLING_INTERVAL_MS);

    this.validationTimeoutId = window.setTimeout(() => {
      this.clearValidationTimers();
      this.isLoading = false;
      // Close Alert
      // this.alertaValidacion(this.isLoading)
      this.manualValidationStarted = false;
    }, LIMITED_VALIDATION_TIMEOUT_MS);
  }

    alertaValidacion(c:boolean) {
    this.colorAdvertencia = "info";
    this.textoAdvertencia = "Validando respuesta del cliente.";
    this.showAdvertencia = c;
    this.loadingAdvertencia = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.cd.detectChanges();
      this.showAdvertencia = false;
    }, 60000); // 1 minuto
  }

  /**
   * Interpreta el status devuelto por validateAuthorizationWhatsApp
   * @param status "ACCEPTED" | "NOT_ACCEPTED" | "IN_PROCESS"
   */
  private handleValidationResponse(status: string): void {
    switch (status) {
      case "ACCEPTED":
        this.dialogService.closeDialog();
        this.clearValidationTimers();
        this.router.navigate(["formulario-perfilamiento"]);
        break;

      case "NOT_ACCEPTED":
        this.dialogService.closeDialog();
        this.clearValidationTimers();
        this.rejectFolder();
        break;

      case "IN_PROCESS":
        break;

      default:
        this.dialogService.closeDialog();
        this.clearValidationTimers();
        break;
    }
  }

  // --- RECHAZAR CARPETA ---

  private async rejectFolder(): Promise<void> {
    try {
      // 1. Cambio de estado de carpeta en backend
      await this.cambioEstadoServ
        .cambioEstadocarpeta(
          +this.numeroCarpeta,
          REJECTION_STATUS_CODE,
          "Rechazo por no autorizar"
        )
        .toPromise();

      // 2. Llamada al servicio que registra el rechazo
      await this.getinfoServ
        .rejectFolder({ carpeta: this.numeroCarpeta, medio: "whatsapp" })
        .toPromise();

      // 3. Mostrar alerta y navegar al home al hacer clic en el botón
      this.dialogService.openDialog(
        "Advertencia",
        " Cliente no autorizo el tratamiento de sus datos. La solicitud ha sido rechazada.",
        false,
        "Ir a inicio",
        () => {
          this.router.navigate(["home"]);
        }
      );
    } catch (error) {
      // Manejo de errores unificado
      this.handleErrorCommon(error, "rechazar carpeta");
    }
  }

  // --- ENVÍO DE SMS ---

  private sendSms(): void {
    this.putFolderUpdateStatus
      .FolderUpdate({ carpeta: +this.numeroCarpeta, newState: NEW_SMS_STATE })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => this.router.navigate([resp.data.routeNewState]),
        (err) => this.handleErrorCommon(err, "enviar SMS")
      );
  }

  // --- CARGAR INFO CLIENTE ---

  private async loadClientInfo(): Promise<void> {
    const resp: any = await this.getinfoServ
      .getInfoClient({ carpeta: this.numeroCarpeta })
      .toPromise();
    const info = resp.data.resultado.infoClient;
    const tries = resp.data.resultado.tries as TryInfo[];
    this.cellphone = info.celular.toString();
    this.enviarWs =
      tries.find((t) => t.tipoValidacion === VALIDATION_TYPE_WS) ||
      this.enviarWs;
    this.enviarOtp =
      tries.find((t) => t.tipoValidacion === VALIDATION_TYPE_OTP) ||
      this.enviarOtp;
  }

  // --- MANEJO DE ERRORES GENERALIZADO ---

  private handleErrorCommon(error: any, context: string): void {
    console.error(`Error en ${context}:`, error);
    this.dialogService.closeDialog();
    this.dialogService.openDialog(
      "Error",
      `No se pudo ${context}.`,
      false,
      "Aceptar"
    );
  }
}
